import { Bot, Check, User } from "lucide-react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/core/components/ui/tooltip";
import { cn } from "@/core/utils/utils";

export const PACK_CHAT_MARK_MODE = {
  /** A manager triaging a queue: does this one need me yet? */
  attention: "attention",
  /** A technician reading their own questions: who owes me a reply? */
  owner: "owner",
} as const;

export type PackChatMarkMode =
  (typeof PACK_CHAT_MARK_MODE)[keyof typeof PACK_CHAT_MARK_MODE];

// `block` is load-bearing: the dot sizes itself, and inside the inline tooltip
// trigger a plain span is non-replaced inline, where width and height do nothing.
const OpenDot = () => (
  <span className="block size-[7px] rounded-full bg-gold-60" aria-hidden />
);

/**
 * The small mark on threads and technician groups. Always a glyph and never a
 * word: it sits in a fixed gutter so a column of them stays scannable, and the
 * state it carries reaches assistive tech through the accessible name.
 *
 * The two surfaces ask different questions of it, so it answers whichever one
 * is in front of the reader:
 *
 * - `attention` (manager queue): the dot for anything still open, the robot for
 *   the ones AI is handling on its own, a check once closed.
 * - `owner` (a technician's own questions): whose turn it is, since the thing
 *   they want to know is whether a person has picked it up yet.
 */
export const PackChatStatusMark = ({
  open,
  label,
  className,
  hasAiMessages,
  needsManager,
  responseOwner,
  mode = PACK_CHAT_MARK_MODE.attention,
  focusable = true,
}: {
  open: boolean;
  label: string;
  className?: string;
  hasAiMessages?: boolean;
  needsManager?: boolean;
  responseOwner?: "ai" | "manager";
  mode?: PackChatMarkMode;
  /** Disable inside row buttons; their accessible name includes the mark. */
  focusable?: boolean;
}) => {
  const owner = mode === PACK_CHAT_MARK_MODE.owner;
  const withManager = responseOwner === "manager";
  /**
   * The robot means AI is answering this one, so it turns on current ownership
   * and never on `hasAiMessages`. AI history outlives the handoff that ended
   * it: keying the glyph off the history put a robot on conversations a manager
   * had already taken over and replied in. Ownership must be stated, not
   * inferred from the absence of a manager, so a response the API did not
   * describe falls through to the neutral dot.
   */
  const aiHandling = responseOwner === "ai" && !needsManager;

  // One decision drives both the glyph and its wording, so the two can never
  // describe different states.
  const kind = !open
    ? "check"
    : aiHandling
      ? "robot"
      : owner && withManager
        ? "person"
        : "dot";

  const mark = {
    check: <Check className="size-3.5 text-green-60" aria-hidden />,
    robot: <Bot className="size-4 text-tertiary-text" aria-hidden />,
    person: (
      <User
        className={cn("size-4", {
          "text-gold-70": needsManager,
          "text-tertiary-text": !needsManager,
        })}
        aria-hidden
      />
    ),
    dot: <OpenDot />,
  }[kind];

  // An AI answer stays part of the history through handoff and closure, so no
  // mark may imply the technician was never given one.
  const aiNote = hasAiMessages ? " Contains AI answers." : "";
  const sentence = label.replace(/\.?$/, ".");
  // The mark stands alone, so its meaning lives in the accessible name.
  const state = {
    check: sentence,
    robot: "AI is answering.",
    person: "A manager is answering.",
    dot: needsManager ? "Needs manager." : sentence,
  }[kind];
  const description = `${state}${aiNote}`;

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center gap-1.5",
        className,
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={focusable ? 0 : undefined}
            role="img"
            aria-label={description}
            data-mark={kind}
          >
            {mark}
          </span>
        </TooltipTrigger>
        <TooltipContent>{description}</TooltipContent>
      </Tooltip>
    </span>
  );
};
